import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import PaginationWithState from "components/Pagination/PaginationWithState";
import { setNotification } from "features/Notification/notificationSlice";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parseFloatData } from "utils/UtilityFuncs";
import AnalysisCardCrud from "./AnalysisCardCrud";

const defaultFilter = { name: "Filter by All", value: null };

export default function AnalysisCard() {
  const dispatch = useDispatch();

  const defaultFormState = {
    id: null,
    analysisCardNo: "",
    inputName: "",
    inputHsCode: "",
    outputName: "",
    outputHsCode: "",
  };

  const defaultOtherEntry = {
    id: null,
    size: "",
    requirement: "",
    useable: "",
    wastage: "",
    wastageRatio: "",
  };

  const [filterBy, setFilterBy] = useState(defaultFilter);
  const [analysisCards, setAnalysisCards] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(defaultFormState);
  const [otherEntries, setOtherEntries] = useState([defaultOtherEntry]);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const getAnalysisCards = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);
        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/manufacturer/efs-analysis-cards?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );

        if (data?.success) {
          setAnalysisCards(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, filterBy.value, pageLimit, pageNumber]
  );

  const deleteAnalysisCard = async (analysisCardNo) => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/delete-efs-analysis-card",
        { analysisCardNo },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        await getAnalysisCards();
        dispatch(
          setNotification({
            message: data?.message || "Deleted",
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message || "Failed to delete",
          type: "error",
        })
      );
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      analysisCardNo: item.analysisCardNo ?? "",
      inputName: item.inputName ?? "",
      inputHsCode: item.inputHsCode ?? "",
      outputName: item.outputName ?? "",
      outputHsCode: item.outputHsCode ?? "",
    });
    setOtherEntries([
      {
        id: item.id,
        size: item.size ?? "",
        requirement: item.requirement ?? "",
        useable: item.useable ?? "",
        wastage: item.wastage ?? "",
        wastageRatio: item.wastageRatio ?? "",
      },
    ]);
    setIsUpdateMode(true);
    setShowModal(true);
  };

  useEffect(() => {
    let timeout = setTimeout(() => {
      getAnalysisCards(
        searchTerm && searchTerm !== ""
          ? { query: searchTerm, page: pageNumber }
          : { clearSearch: true, page: pageNumber }
      );
    }, 50);
    return () => clearTimeout(timeout);
  }, [pageNumber, pageLimit, filterBy, searchTerm, getAnalysisCards]);

  const filters = [
    { value: "outputName", name: "Output Good Name" },
    { value: "outputHsCode", name: "Output HS Code" },
    { value: "inputName", name: "Input Good Name" },
    { value: "inputHsCode", name: "Input HS Code" },
    { value: "size", name: "Size" },
  ];

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy(defaultFilter)}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "Analysis Card No", value: "analysisCardNo" },
    { name: "Input Good Name", value: "inputName" },
    { name: "Input HS Code", value: "inputHsCode" },
    { name: "Output Good Name", value: "outputName" },
    { name: "Output HS Code", value: "outputHsCode" },
    { name: "Actions", value: "actions" },
  ];

  const table_body = (item) => (
    <tr key={item.id}>
      <td className="align-middle py-1">{item.analysisCardNo || "-"}</td>
      <td className="align-middle py-1">{item.inputName || "-"}</td>
      <td className="align-middle py-1">{item.inputHsCode || "-"}</td>
      <td className="align-middle py-1">{item.outputName || "-"}</td>
      <td className="align-middle py-1">{item.outputHsCode || "-"}</td>
      <td className="align-middle py-1 text-end table-action">
        <button
          disabled={loading}
          onClick={() => handleEdit(item)}
          className="btn btn-sm px-1"
        >
          <i className="mdi mdi-pencil"></i>
        </button>
        <DeleteModalButton
          btnClass={"btn btn-sm px-1"}
          deleteFunc={deleteAnalysisCard}
          deleteParams={item.analysisCardNo}
          disableConditions={loading}
        />
      </td>
    </tr>
  );

  return (
    <div className="p-2" style={{ marginLeft: 260, height: "100vh" }}>
      <h2 className="mb-3 ps-2">Analysis Card</h2>
      {loading && <Spinner />}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div className="ps-2">
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getAnalysisCards({ query: searchTerm, page: 1 });
              } else {
                getAnalysisCards({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getAnalysisCards({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          <div className="">
            <button
              onClick={() => {
                setShowModal(true);
                setFormData(defaultFormState);
                setOtherEntries([defaultOtherEntry]);
                setIsUpdateMode(false);
              }}
              type="button"
              className="btn btn-primary  ms-1"
            >
              Add Analysis Card
            </button>
          </div>
        </div>
      </div>

      <div
        className="px-1 flex-grow-1 d-flex"
        style={{ minHeight: "50vh", borderRadius: 15 }}
      >
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={analysisCards}
            disableSorting={false}
            actionsOnEnd={true}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      <Modal show={showModal} size="xl" onHide={() => setShowModal(false)}>
        <Modal.Body
          className="shadow-lg bg-light px-3 "
          style={{ borderRadius: 15 }}
        >
          <AnalysisCardCrud
            setShowModal={setShowModal}
            isUpdateMode={isUpdateMode}
            formData={formData}
            setFormData={setFormData}
            refreshFunc={getAnalysisCards}
            setIsUpdateMode={setIsUpdateMode}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
